import { createContext, useContext, useEffect, useState } from "react";
import { SingleProductModal } from "../components/SingleProductModal/SingleProductModal";
import { ProductOptionType } from "../Enum/ProductOptionType";
import { useCart } from "./CartContext";
import { useServer } from "./ServerContext";
import { cartEndpoints } from "../env";
import { useCookies } from "./CookiesContext";
import { useBus } from "react-bus";

const SingleProductContext = createContext(undefined);

export const SingleProductProvider = ({children}) => {
    //HOOKS
    const { postRequest } = useServer();
    const { getCookie } = useCookies();
    const bus = useBus();
    
    //STATES
    const [singleProduct, setSingleProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [comment, setComment] = useState("");
    const [options, setOptions] = useState(null);
    const [optionsErrors, setOptionsErrors] = useState({});
    const [optionsPool, setOptionsPool] = useState(null);
    const [choicesPool, setChoicesPool] = useState(null);
    const [total, setTotal] = useState(0);
    const [actionCreate, setActionCreate] = useState(true);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [itemId, setItemId] = useState(null);

    //FUNCTIONS
    const calculateTotal = () => {
        let total = singleProduct.pricing.price;

        if (optionsPool !== null)
        {
            Object.keys(optionsPool).forEach((optionId) => {
                const option = optionsPool[optionId];
    
                if (option.type === ProductOptionType.SELECTION)
                {
                    let choice = choicesPool[options[optionId].choice];
                    total += choice.price;
                }
                else if (option.type === ProductOptionType.EXTRA)
                {
                    const choices = options[optionId].choices;

                    for (let i = 0; i < choices.length; i++)
                    {
                        const choice = choicesPool[choices[i]];
                        total += choice.price;
                    }
                }
            });
        }
        
        total *= qty;
        setTotal(total);
    }

    const makeOptions = () => {
        let options = {};
        let choices = {};
        let optionsPool = {};

        singleProduct.options.options.forEach((option) => {
            optionsPool[option.id] = option;
            let optionToAdd = {
                id: option.id,
            }

            if (option.type === ProductOptionType.SELECTION)
            {
                optionToAdd.choice = -1;
            }
            else if (option.type === ProductOptionType.EXTRA)
            {
                optionToAdd.choices = [];
            }

            option.choices.map((choice) => {
                choices[choice.id] = choice;
                if (choice.default)
                {
                    if (option.type === ProductOptionType.SELECTION)
                    {
                        optionToAdd.choice = choice.id;
                    }
                    else if (option.type === ProductOptionType.EXTRA)
                    {
                        optionToAdd.choices = [choice.id];
                    }
                }
            });

            options[optionToAdd.id] = optionToAdd;
        });

        setOptions(options);
        setChoicesPool(choices);
        setOptionsPool(optionsPool);
    }

    const setSelectionChoice = (optionId, choice) => {
        setOptions({...options, [optionId]: {id: optionId, choice}});
    }

    const setSelectedExtra = (optionId, choice) => {
        const option = optionsPool[optionId];
        let choices = options[optionId].choices;

        if (choices.includes(choice))
        {
            choices.splice(choices.indexOf(choice), 1);
        }
        else if (choices.length === option.max && option.max > 0)
        {
            return;
            //choices[choices.length - 1] = choice;
        }
        else
        {
            choices.push(choice);
        }

        setOptions({...options, [optionId]: {id: optionId, choices}});
    }

    const postAction = () => {
        if (loading)
        {
            return;
        }

        if (!validate())
        {
            return;
        }

        setErrorMsg("");

        if (actionCreate)
        {
            postSave();
        }
        else
        {
            postUpdate();
        }
    }

    const postSave = () => {
        setLoading(true);

        let data = getData();
        
        postRequest(cartEndpoints.add_item, data, (res) => {
            setLoading(false);

            if (res[0])
            {
                res = res[1];

                if (res.success)
                {
                    setSingleProduct(null);
                    bus.emit("cart_update", res.cart);
                }
                else
                {
                    if (res.error_code === "product_unavailable")
                    {
                        setErrorMsg("The product is currently unavailable.");
                    }
                    else
                    {
                        setErrorMsg("Server Error, please try again in a bit.");
                    }
                }
            }
            else
            {
                setErrorMsg("Server Error, please try again in a bit.");
            }
        });
    }

    const postUpdate = () => {
        setLoading(true);

        let data = getData();
        data.item_id = itemId;
        
        postRequest(cartEndpoints.update_item, data, (res) => {
            setLoading(false);

            if (res[0])
            {
                res = res[1];

                if (res.success)
                {
                    setSingleProduct(null);
                    bus.emit("cart_update", res.cart);
                }
                else
                {
                    if (res.error_code === "product_unavailable")
                    {
                        setErrorMsg("The product is currently unavailable.");
                    }
                    else
                    {
                        setErrorMsg("Server Error, please try again in a bit.");
                    }
                }
            }
            else
            {
                setErrorMsg("Server Error, please try again in a bit.");
            }
        });
    }
    
    const validate = () => {
        let valid = true;
        let errors = {};

        for (let i = 0; i < singleProduct.options.options.length; i++)
        {
            let option = singleProduct.options.options[i];

            if (option.type === ProductOptionType.SELECTION)
            {
                if (options[option.id].choice === "")
                {
                    valid = false;
                    errors[option.id] = "You have to select one option.";
                }
            }
            else if (option.type === ProductOptionType.EXTRA)
            {
                if (option.min > 0 && options[option.id].choices.length < option.min)
                {
                    valid = false;
                    errors[option.id] = "You have to select at least " + option.min + " options.";
                }
                if (option.max > 0 && options[option.id].choices.length > option.max)
                {
                    valid = false;
                    errors[option.id] = "You can select up to " + option.max + " options.";
                }
            } 
        }

        setOptionsErrors(errors);
        return valid;
    }

    const getData = () => {
        let data = {
            product_id: singleProduct.id,
            qty,
            comments: comment,
            options: [

            ],
            cart_token: getCookie("cart_token"),
        };

        Object.keys(options).forEach((key) => {
            data.options.push({
                id: options[key].id,
                choices: optionsPool[key].type === ProductOptionType.SELECTION ? [options[key].choice] : options[key].choices,
            });
        });

        return data;
    }

    const resetInputs = () => {
        setQty(1);
        setComment("");
        setOptions(null);
        setOptionsErrors({});
        setOptionsPool(null);
        setChoicesPool(null);
        setActionCreate(true);
        setTotal(0);
        setErrorMsg("");
        setItemId(null);
    }

    const openProductUpdate = (item) => {
        setItemId(item.id);
        setSingleProduct(item.product);
        setQty(item.qty);
        setComment(item.comments);
        setActionCreate(false);

        let newOptions = {};

        for (let i = 0; i < item.options.length; i++)
        {
            const option = item.options[i];

            if (option.type === ProductOptionType.SELECTION)
            {
                newOptions[option.option_id] = {
                    id: option.option_id,
                    choice: option.choices[0],
                }
            }
            else if (option.type === ProductOptionType.EXTRA)
            {
                newOptions[option.option_id] = {
                    id: option.option_id,
                    choices: option.choices
                }
            }
        }

        setTimeout(() => {
            setOptions(newOptions);
        }, 50);

        setOptions(newOptions);
    }

    //EFFECTS
    useEffect(() => {
        if (singleProduct === null)
        {
            resetInputs();
        }
        else
        {
            makeOptions();
        }
    }, [singleProduct]);

    useEffect(() => {
        if (singleProduct !== null)
        {
            calculateTotal();
        }
    }, [singleProduct, qty, options]);

    const value = {
        singleProduct,
        setSingleProduct,
        qty,
        setQty,
        comment,
        setComment,
        options,
        setSelectionChoice,
        setSelectedExtra,
        actionCreate,
        setActionCreate,
        total,
        postAction,
        optionsErrors,
        loading,
        errorMsg,
        openProductUpdate
    }

    return (
        <SingleProductContext.Provider value={value}>
            {children}
            <SingleProductModal />
        </SingleProductContext.Provider>
    );
}

export const useSingleProduct = () => useContext(SingleProductContext);